import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Users } from "lucide-react";

import { api } from "../api";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import CountrySelect from "./CountrySelect";
import { DEFAULT_COUNTRY } from "../data/countries";

/**
 * ShareBalanceCard — P2P wallet transfer.
 *
 * Uses the same CountrySelect + phone field as Login, plus a small amount
 * input. Calls `POST /api/wallet/share` and refreshes the parent balance via
 * `onBalanceChange` (or `updateBalance` from AuthContext as fallback).
 */
export default function ShareBalanceCard({ onBalanceChange }) {
  const { user, lang, config, updateBalance } = useAuth();
  const currency = config?.currency_symbol || "₹";
  const balance = user?.wallet_balance ?? 0;

  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    // Basic client-side validation — mirror the Login constraints so users
    // get instant feedback before the roundtrip.
    const cleanPhone = String(phone || "").replace(/\D/g, "");
    if (cleanPhone.length < 6) {
      toast.error(t(lang, "share_recipient_invalid"));
      return;
    }
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error(t(lang, "share_invalid_amount"));
      return;
    }
    if (amt > balance) {
      toast.error(t(lang, "share_insufficient_balance"));
      return;
    }

    setBusy(true);
    try {
      const r = await api.post("/wallet/share", {
        country_dial_code: country.dial,
        phone: cleanPhone,
        amount: amt,
      });
      const d = r.data || {};
      if (typeof d.wallet_balance === "number") {
        updateBalance(d.wallet_balance);
        onBalanceChange?.(d.wallet_balance);
      }
      toast.success(
        t(lang, "share_success")
          .replace("{amount}", `${currency}${d.amount ?? amt}`)
          .replace("{phone}", `${country.dial} ${cleanPhone}`)
      );
      setPhone("");
      setAmount("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      data-testid="share-balance-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 bg-white rounded-3xl p-5 border-4 border-slate-900/5 shadow-[0_6px_0_rgba(0,0,0,0.05)]"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-sky-400 flex items-center justify-center">
          <Users size={26} strokeWidth={2.5} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-xl font-bold text-slate-800">
            {t(lang, "share_balance_title")}
          </h3>
          <p className="text-xs text-slate-500 font-semibold">
            {t(lang, "share_balance_desc")}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-4 space-y-3">
        {/* Phone row (mirrors Login layout) */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
            {t(lang, "share_recipient_phone")}
          </label>
          <div className="mt-1 flex gap-2">
            <CountrySelect value={country} onChange={setCountry} />
            <input
              data-testid="share-phone-input"
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              placeholder={t(lang, "share_phone_placeholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              maxLength={15}
              className="flex-1 min-w-0 w-full px-4 h-12 text-lg font-bold text-slate-900 bg-[#f8fafc] outline-none placeholder:text-slate-400 rounded-2xl border-2 border-slate-900/10 focus:border-sky-400 focus:bg-white transition-colors"
            />
          </div>
        </div>

        {/* Amount row */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
            {t(lang, "share_amount")}
          </label>
          <div className="mt-1 relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg pointer-events-none">
              {currency}
            </span>
            <input
              data-testid="share-amount-input"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              className="w-full pl-9 pr-4 h-12 text-lg font-bold text-slate-900 bg-[#f8fafc] outline-none placeholder:text-slate-400 rounded-2xl border-2 border-slate-900/10 focus:border-sky-400 focus:bg-white transition-colors tabular-nums"
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="share-send-btn"
          disabled={busy || !phone || !amount}
          className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white font-heading text-lg font-bold uppercase tracking-wide py-3.5 rounded-2xl btn-squish flex items-center justify-center gap-2 shadow-[0_4px_0_rgba(0,0,0,0.12)]"
        >
          {busy ? (
            <>
              <Loader2 size={20} strokeWidth={3} className="animate-spin" />
              {t(lang, "processing")}
            </>
          ) : (
            <>
              <Send size={20} strokeWidth={2.75} />
              {t(lang, "share_send_btn")}
            </>
          )}
        </button>
      </form>
    </motion.section>
  );
}
